import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table'
import { TextStyle } from '@tiptap/extension-text-style'
import Highlight from '@tiptap/extension-highlight'

import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Quote, Minus, Link as LinkIcon,
  Heading1, Heading2, Heading3,
  Table as TableIcon, Image as ImageIcon,
  Highlighter, Undo, Redo, Code, CodeSquare,
} from 'lucide-react'

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useEditor, EditorContent } from '@tiptap/react'
import { useEffect } from 'react'

// ─── Toolbar helpers ──────────────────────────────────────────────────────────

function ToolbarBtn({
  onClick, active, disabled, title, children,
}: {
  onClick: () => void
  active?: boolean
  disabled?: boolean
  title: string
  children: React.ReactNode
}) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); onClick() }}
            disabled={disabled}
            className={`h-7 w-7 flex items-center justify-center rounded transition-colors
              ${active
                ? 'bg-gray-800 text-white'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200'
              } disabled:opacity-30 disabled:cursor-not-allowed`}
          >
            {children}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">{title}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function ToolbarDivider() {
  return <div className="w-px h-5 bg-gray-200 mx-0.5 shrink-0" />
}

function EditorToolbar({ editor }: { editor: ReturnType<typeof useEditor> }) {
  if (!editor) return null

  const setLink = () => {
    const prev = editor.getAttributes('link').href ?? ''
    const url  = window.prompt('URL', prev)
    if (url === null) return
    if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank' }).run()
  }

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-3 py-2 border-b border-gray-200 bg-gray-50">
      <ToolbarBtn onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()} title="Undo"><Undo size={14} /></ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()} title="Redo"><Redo size={14} /></ToolbarBtn>

      <ToolbarDivider />

      <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        active={editor.isActive('heading', { level: 1 })} title="Heading 1"><Heading1 size={14} /></ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        active={editor.isActive('heading', { level: 2 })} title="Heading 2"><Heading2 size={14} /></ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        active={editor.isActive('heading', { level: 3 })} title="Heading 3"><Heading3 size={14} /></ToolbarBtn>

      <ToolbarDivider />

      <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive('bold')} title="Bold"><Bold size={14} /></ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive('italic')} title="Italic"><Italic size={14} /></ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleUnderline().run()}
        active={editor.isActive('underline')} title="Underline"><UnderlineIcon size={14} /></ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleStrike().run()}
        active={editor.isActive('strike')} title="Strikethrough"><Strikethrough size={14} /></ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleHighlight().run()}
        active={editor.isActive('highlight')} title="Highlight"><Highlighter size={14} /></ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleCode().run()}
        active={editor.isActive('code')} title="Inline code"><Code size={14} /></ToolbarBtn>

      <ToolbarDivider />

      <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign('left').run()}
        active={editor.isActive({ textAlign: 'left' })} title="Align left"><AlignLeft size={14} /></ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign('center').run()}
        active={editor.isActive({ textAlign: 'center' })} title="Align center"><AlignCenter size={14} /></ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign('right').run()}
        active={editor.isActive({ textAlign: 'right' })} title="Align right"><AlignRight size={14} /></ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign('justify').run()}
        active={editor.isActive({ textAlign: 'justify' })} title="Justify"><AlignJustify size={14} /></ToolbarBtn>

      <ToolbarDivider />

      <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive('bulletList')} title="Bullet list"><List size={14} /></ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive('orderedList')} title="Numbered list"><ListOrdered size={14} /></ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()}
        active={editor.isActive('blockquote')} title="Blockquote"><Quote size={14} /></ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        active={editor.isActive('codeBlock')} title="Code block"><CodeSquare size={14} /></ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Horizontal rule"><Minus size={14} /></ToolbarBtn>

      <ToolbarDivider />

      <ToolbarBtn onClick={setLink} active={editor.isActive('link')} title="Insert / edit link">
        <LinkIcon size={14} /></ToolbarBtn>
      <ToolbarBtn onClick={() => {
        const url = window.prompt('Image URL')
        if (url) editor.chain().focus().setImage({ src: url }).run()
      }} title="Insert image by URL"><ImageIcon size={14} /></ToolbarBtn>
      <ToolbarBtn onClick={() =>
        editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
      } title="Insert table"><TableIcon size={14} /></ToolbarBtn>
    </div>
  )
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface RichTextEditorProps {
  value: string
  onChange: (html: string) => void
  minHeight?: string
  disabled?: boolean
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function RichTextEditor({
  value,
  onChange,
  minHeight = '180px',
  disabled = false,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false }),
      Image,
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      TextStyle,
      Highlight,
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    editorProps: {
      attributes: {
        class: [
          'prose prose-sm max-w-none px-4 py-3 focus:outline-none text-gray-900',
          'prose-headings:font-semibold prose-headings:tracking-tight prose-headings:text-gray-900',
          'prose-a:text-blue-600 prose-a:underline',
          'prose-blockquote:border-l-4 prose-blockquote:border-gray-300',
          'prose-blockquote:pl-4 prose-blockquote:text-gray-500 prose-blockquote:italic',
          'prose-code:bg-gray-100 prose-code:rounded prose-code:px-1 prose-code:text-[0.85em] prose-code:text-gray-800',
          'prose-pre:bg-gray-100 prose-pre:rounded-lg prose-pre:p-4',
          'prose-table:w-full prose-td:border prose-td:border-gray-200 prose-td:p-2',
          'prose-th:border prose-th:border-gray-200 prose-th:p-2 prose-th:bg-gray-100 prose-th:font-semibold',
          'prose-img:rounded-lg prose-img:max-w-full',
        ].join(' '),
        style: `min-height: ${minHeight}`,
      },
    },
  })

  // Sync external value changes (e.g. when data loads from API)
  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value)
    }
  }, [value, editor])

  // Sync disabled state
  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    editor.setEditable(!disabled)
  }, [disabled, editor])

  return (
    <div className={`rounded-lg border border-gray-200 bg-white overflow-hidden ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
      <EditorToolbar editor={editor} />
      <EditorContent editor={editor} />
      <div className="px-4 py-1.5 border-t border-gray-200 bg-gray-50">
        <p className="text-[11px] text-gray-400">
          {editor?.getText().trim().split(/\s+/).filter(Boolean).length ?? 0} words
        </p>
      </div>
    </div>
  )
}
